/**
 * The route registry: every endpoint declared once, in one place.
 *
 * This is the single source the OpenAPI document is generated from, and it is also what the server
 * will be checked against and what the client's method signatures are derived from. Declaring an
 * endpoint here and separately in a controller is the thing this file exists to avoid: two
 * declarations that agree on the day they are written and drift afterwards.
 *
 * Why the registry is data and not code: a route described as an object can be measured. The
 * generator reads it, the client generator reads it, and a test can assert that every route has a
 * response schema. A route described as a decorated method is only inspectable by running the
 * framework, which is one more reason the API is not built on decorators — see ADR-008.
 *
 * Order matters for the server: `/places/lookup` and `/places/map-link` are static and must be matched
 * before `/places/:id`, or `lookup` is read as an id. The registry keeps them adjacent and in that
 * order so the server can register them as written.
 */

import { z } from "zod";

import type { ProblemStatus } from "./errors.ts";
import { AreaList, AreaResponse, CreateAreaRequest, Id, PageQuery } from "./common.ts";
import {
  AuthSession,
  GoogleSignInRequest,
  GuestClaimRequest,
  MeResponse,
  PasswordLoginRequest,
  PasswordRegisterRequest,
  RefreshRequest,
  SessionList,
  UpdateMeRequest,
} from "./auth.ts";
import {
  AiPlaceLookupRequest,
  AiPlaceLookupResponse,
  CreatePlaceRequest,
  CreatePlaceResponse,
  DeletePlaceResponse,
  DuplicateCheckRequest,
  DuplicateCheckResponse,
  DuplicatePairList,
  MergePlaceRequest,
  MergePlaceResponse,
  Place,
  PlaceCategory,
  PlaceListQuery,
  PlaceLookupResponse,
  ResolveMapLinkResponse,
  PlaceSummaryPage,
  ProviderStatus,
  UpdatePlaceRequest,
} from "./places.ts";
import { RoutePreviewRequest, RoutePreviewResponse } from "./routing.ts";
import {
  CreateFundParticipantRequest,
  FundEntry,
  FundParticipant,
  RecordFundContributionRequest,
  RecordFundExpenseRequest,
  TripFundResponse,
  UpdateFundEntryRequest,
  UpdateFundParticipantRequest,
} from "./fund.ts";
import {
  CreatePlansRequest,
  CreatePlansResponse,
  CreateTripRequest,
  PlanExplanationsResponse,
  PlanVariant,
  PlanVariantDetail,
  PutDayOrderRequest,
  SelectPlanRequest,
  TripPage,
  TripPlansResponse,
  TripResponse,
  TripRoutesResponse,
  TripWeatherResponse,
  UpdateTripRequest,
} from "./trips.ts";
import { MetaResponse } from "./meta.ts";

export type HttpMethod = "get" | "post" | "put" | "patch" | "delete";

/** One response. `schema` is absent only for a bodyless status such as 204. */
export interface ResponseSpec {
  description: string;
  schema?: z.ZodType;
}

export interface RequestSpec {
  /** Path parameters. Their names must appear as `:name` in `path`. */
  params?: z.ZodObject;
  query?: z.ZodObject;
  body?: z.ZodType;
}

export interface RouteDefinition {
  method: HttpMethod;
  /** Express-style `:param`, converted to `{param}` for the document. */
  path: string;
  /** Stable identifier. The generated client's method name comes from this, so renaming it is a breaking change. */
  operationId: string;
  summary: string;
  description?: string;
  tags: string[];
  /** Whether a bearer token is required. `false` for `/meta`, which must work with a broken session. */
  auth: boolean;
  /**
   * Whether `Idempotency-Key` is honoured.
   *
   * The header is advertised in the document only where it is implemented. Advertising it globally
   * would let a client send it against an endpoint that ignores it and then believe a retry is safe
   * when it is not.
   */
  idempotent?: boolean;
  request?: RequestSpec;
  responses: Record<number, ResponseSpec>;
  /** Error statuses beyond the default set for this route. */
  errors?: ProblemStatus[];
}

/** Errors every authenticated route can return, at minimum. */
const BASE_ERRORS: ProblemStatus[] = [400, 401, 403, 404, 429];

/** A path with one id parameter. */
const byId = z.object({ id: Id });

/** `/trips/:id/fund/participants/:participantId`. */
const byParticipant = z.object({ id: Id, participantId: Id });

/** `/trips/:id/fund/contributions/:entryId` and `.../expenses/:entryId`. */
const byEntry = z.object({ id: Id, entryId: Id });

/**
 * `as const satisfies` and not a plain annotation.
 *
 * A plain `readonly RouteDefinition[]` erases the specifics: `operationId` widens to `string`, and
 * the zod schemas behind `request` and `responses` are lost behind `z.ZodType`. `as const` keeps
 * them, which is what lets the API client's method names and parameter types be *derived* from this
 * array instead of declared a second time. `satisfies` keeps the check that every element really is
 * a `RouteDefinition`, so nothing is lost by dropping the annotation.
 */
export const ROUTES = [
  // -------------------------------------------------------------------------------- identity
  {
    method: "post",
    path: "/auth/guest",
    operationId: "signInAsGuest",
    summary: "Start as a guest",
    description:
      "Creates an anonymous user and a session. Takes no request body, so no fields are declared — " +
      "an empty object schema would document a contract the caller never sends and would then have to " +
      "be validated against a body that does not arrive.",
    tags: ["auth"],
    auth: false,
    responses: { 201: { schema: AuthSession, description: "A session for a new guest account." } },
  },
  {
    method: "post",
    path: "/auth/google",
    operationId: "signInWithGoogle",
    summary: "Sign in with Google",
    description:
      "Verifies the Google ID token against Google's JWKS, including its audience, issuer and expiry. " +
      "An unverified email is surfaced to the account-linking layer rather than silently trusted.",
    tags: ["auth"],
    auth: false,
    request: { body: GoogleSignInRequest },
    responses: { 200: { schema: AuthSession, description: "A session for the account the token identifies." } },
    errors: [409],
  },
  {
    method: "post",
    path: "/auth/password/register",
    operationId: "registerWithPassword",
    summary: "Create an account with an email and password",
    description:
      "The password policy is enforced server-side with the email and display name as context, because " +
      "a password containing the user's own address is weak in a way a length rule cannot see.",
    tags: ["auth"],
    auth: false,
    request: { body: PasswordRegisterRequest },
    responses: { 201: { schema: AuthSession, description: "A session for the new account." } },
    errors: [409],
  },
  {
    method: "post",
    path: "/auth/password/login",
    operationId: "loginWithPassword",
    summary: "Sign in with an email and password",
    tags: ["auth"],
    auth: false,
    request: { body: PasswordLoginRequest },
    responses: { 200: { schema: AuthSession, description: "A session for the account." } },
  },
  {
    method: "post",
    path: "/auth/guest/claim",
    operationId: "claimGuestAccount",
    summary: "Attach credentials to the current guest",
    description:
      "Keeps the guest's user row and everything it owns, and adds an identity to it (AC-1.2). " +
      "The alternative — creating a second user and migrating — is a migration that can fail halfway " +
      "for the user who has done the most work.",
    tags: ["auth"],
    auth: true,
    request: { body: GuestClaimRequest },
    responses: { 200: { schema: AuthSession, description: "A session for the same account, now with credentials." } },
    errors: [409],
  },
  {
    method: "post",
    path: "/auth/refresh",
    operationId: "refreshSession",
    summary: "Rotate the refresh token and get a new access token",
    description:
      "Rotates on every call: the new session records `rotated_from` and the previous row is revoked " +
      "rather than deleted. Presenting an already-revoked token therefore means a stolen token is being " +
      "replayed, and the whole rotation family is revoked in response.",
    tags: ["auth"],
    auth: false,
    request: { body: RefreshRequest },
    responses: { 200: { schema: AuthSession, description: "A new session. The old refresh token is now dead." } },
  },
  {
    method: "post",
    path: "/auth/logout",
    operationId: "logout",
    summary: "End this session",
    description:
      "Revokes the session the presented access token names, via its `sid` claim. No body, so sign-out " +
      "is identical for a browser and a native app despite their refresh tokens being stored differently.",
    tags: ["auth"],
    auth: true,
    responses: { 204: { description: "This session is revoked." } },
  },
  {
    method: "post",
    path: "/auth/logout-all",
    operationId: "logoutAll",
    summary: "End every session",
    description:
      "For the case where the user thinks they are signed in somewhere they should not be. " +
      "Revokes every live session for the account.",
    tags: ["auth"],
    auth: true,
    responses: { 204: { description: "Every session for this account is revoked." } },
  },
  {
    method: "get",
    path: "/auth/sessions",
    operationId: "listSessions",
    summary: "Signed-in devices",
    description:
      "The reason sessions are stored server-side is so a user can sign out a phone they no longer " +
      "have, which makes this a day-one security feature rather than a later nicety. `current` marks " +
      "the session making this request.",
    tags: ["auth"],
    auth: true,
    responses: { 200: { schema: SessionList, description: "Every live session, newest first." } },
  },
  {
    method: "delete",
    path: "/auth/sessions/:id",
    operationId: "revokeSession",
    summary: "Sign one device out",
    description: "Scoped to the caller's own sessions, so a session id from another account revokes nothing.",
    tags: ["auth"],
    auth: true,
    request: { params: z.object({ id: Id }) },
    responses: { 204: { description: "That session is revoked." } },
    errors: [404],
  },

  // --------------------------------------------------------------------------------- profile
  {
    method: "get",
    path: "/me",
    operationId: "getMe",
    summary: "The signed-in account",
    tags: ["profile"],
    auth: true,
    responses: { 200: { schema: MeResponse, description: "The account." } },
  },
  {
    method: "patch",
    path: "/me",
    operationId: "updateMe",
    summary: "Edit the account",
    description: "Omitted fields are left unchanged. `travel_style` replaces the stored map rather than merging it.",
    tags: ["profile"],
    auth: true,
    request: { body: UpdateMeRequest },
    responses: { 200: { schema: MeResponse, description: "The updated account." } },
  },
  {
    method: "delete",
    path: "/me",
    operationId: "deleteMe",
    summary: "Delete the account and everything in it",
    description:
      "Irreversible, and AC-1.3 tests that the next launch shows onboarding rather than a cached place " +
      "list. Returns no body: there is nothing useful to say, and reporting counts would invite a " +
      "client to show them for an action the user has already confirmed.",
    tags: ["profile"],
    auth: true,
    responses: { 204: { description: "The account and its data are gone." } },
  },

  // ------------------------------------------------------------------------------------ meta
  {
    method: "get",
    path: "/meta",
    operationId: "getMeta",
    summary: "What this server supports and what changed",
    description:
      "Lets a client that cannot force-update itself learn the API version, the oldest build still " +
      "supported, which features are switched on, and the enforced limits. Unauthenticated on purpose: " +
      "a client whose session is broken is exactly the one that needs to read this.",
    tags: ["meta"],
    auth: false,
    responses: {
      200: { schema: MetaResponse, description: "Server capabilities." },
    },
  },

  // ------------------------------------------------------------------------------- places
  {
    method: "get",
    path: "/places",
    operationId: "listPlaces",
    summary: "The caller's saved places",
    description:
      "Cursor-paginated. `q` matches name, address, area names and note with diacritics folded, so " +
      "`cau dat` finds a place in `Cầu Đất`.",
    tags: ["places"],
    auth: true,
    request: { query: PlaceListQuery },
    responses: {
      200: { schema: PlaceSummaryPage, description: "A page of saved places." },
    },
  },
  {
    method: "post",
    path: "/places",
    operationId: "createPlace",
    summary: "Save a place",
    description:
      "Only `name` is required (AC-3.1). No network request is made to any supplied `sources[].url` — " +
      "the server derives nothing from it (ADR-004, AC-3.3).",
    tags: ["places"],
    auth: true,
    idempotent: true,
    request: { body: CreatePlaceRequest },
    responses: {
      201: { schema: CreatePlaceResponse, description: "The saved place." },
    },
    errors: [409],
  },
  // Static paths before `/places/:id`, or `search` is matched as an id.
  {
    method: "get",
    path: "/places/duplicates",
    operationId: "listDuplicatePairs",
    summary: "Pairs of saved places the duplicate rule can see",
    description:
      "The save-time warning cannot help places saved before it existed, so this is the surface for " +
      "pairs that already exist: the same database rule, run over the caller's own saved places.\n\n" +
      "**A partial list, and the response is shaped so a client must not overclaim it.** The rule " +
      "matches name similarity plus proximity, so measured it cannot see a one-letter spelling " +
      "difference, a street suffix, an abbreviation, or a pair where one place has no coordinate. " +
      "Each pair names the earlier-created place as `keep`, so the merge direction is the server's " +
      "answer rather than the client's guess.\n\n" +
      "**Retained without a UI caller.** The manage list's manual review section was removed, so the " +
      "app no longer reads this; the route stays anyway, because a published route is a promise to " +
      "clients and removing one is a breaking change that needs a version bump. It is also the query a " +
      "merge reached from a place's own card would use, for the pairs the rule cannot see.",
    tags: ["places"],
    auth: true,
    responses: {
      200: { schema: DuplicatePairList, description: "Pairs the rule can see, oldest first." },
    },
  },
  {
    method: "post",
    path: "/places/duplicates",
    operationId: "checkPlaceDuplicates",
    summary: "Check a draft for duplicates before saving",
    description:
      "The pre-save warning behind AC-3.5, callable while the user is still on the capture form. " +
      "Backed by the same `find_duplicate_places` the merge-time paths use, so the warning and any " +
      "later check cannot disagree about what counts as a duplicate.\n\n" +
      "**POST rather than a GET, and the reason is the body.** What it carries is a *draft* place — a " +
      "name and a coordinate that are not saved and have no identity to be fetched by — and a query " +
      "string would write a Vietnamese place name into every access log and cache on the way. A body " +
      "is the honest shape for a transient question; AC-3.5 names it POST for the same reason.\n\n" +
      "An empty `duplicates` is a definite no: the rule ran and matched nothing of the caller's. A " +
      "draft with no coordinate cannot be checked — proximity is half the rule — and is refused here " +
      "rather than answered by name alone, because a name-only match is a guess, not a fact.",
    tags: ["places"],
    auth: true,
    request: { body: DuplicateCheckRequest },
    responses: {
      200: { schema: DuplicateCheckResponse, description: "The caller's saved places the draft may duplicate." },
    },
  },
  {
    method: "get",
    path: "/places/map-link",
    operationId: "resolveMapLink",
    summary: "What a Google Maps link points at",
    description:
      "Resolves a Google Maps share link into the coordinate it carries, so a user who already has the " +
      "place open in Maps can hand over the location instead of asking the geocoder to find what it may " +
      "not have. Measured recall, which is why this exists: of ten real Đà Lạt venues the free geocoder " +
      "resolves seven, and the two it cannot find at all are ordinary cafés a user can see in Maps.\n\n" +
      "**Long link or short link, and only one of them costs a request.** A full `google.com/maps/...` " +
      "URL holds its coordinate in the string, so the client parses it locally and never calls this. A " +
      "`maps.app.goo.gl` share link holds nothing — its content is a 302 — and a browser cannot read a " +
      "cross-origin `Location` header, which is the only reason this endpoint needs to exist. It reads " +
      "that header and nothing else: the redirect is not followed, no page is requested, and nothing is " +
      "stored.\n\n" +
      "`status` distinguishes 'this link has no coordinate' from 'Google was unreachable', because only " +
      "one of those is the caller's to act on.",
    tags: ["places"],
    auth: true,
    request: {
      query: z.object({
        url: z.string().min(1).describe("The Google Maps link the user pasted, exactly as pasted."),
      }),
    },
    responses: {
      200: { schema: ResolveMapLinkResponse, description: "The coordinate the link carries, or the reason it gave none." },
    },
    errors: [400],
  },
  {
    method: "get",
    path: "/places/lookup",
    operationId: "lookupPlace",
    summary: "Look a place up with the provider, for coordinates",
    description:
      "Provider-only: makes no claim about the caller's own places. Answers carry `match_quality`, " +
      "because a plausible wrong coordinate is worse than none — measured, 1 200 m of error is enough " +
      "to reorder a day, and 2 500 m makes the route 7.5 km worse.\n\n" +
      "`lat`/`lon` are the search centre, and they are more than a ranking hint: the keyed providers " +
      "(Google and Goong) refuse to answer without one, because the same input asked from nowhere can " +
      "come back with an unrelated place in another province. A request that omits them is still " +
      "answered — from the keyless OSM path — so an older client keeps working and a caller with no " +
      "location is not told the service is down. `distance_m` on each result is measured from this " +
      "centre, and is null when none was sent.",
    tags: ["places"],
    auth: true,
    request: {
      query: z.object({
        q: z.string().min(1),
        area: z.string().optional().describe("Appended to the provider query. Measured to beat a bounding box."),
        lat: z.coerce.number().min(-90).max(90).optional()
          .describe("Centre latitude. Google and Goong will not answer without a centre; OSM answers either way."),
        lon: z.coerce.number().min(-180).max(180).optional()
          .describe("Centre longitude. Sent with `lat`; one without the other is ignored."),
      }),
    },
    responses: {
      200: { schema: PlaceLookupResponse, description: "Provider candidates with confidence." },
    },
    errors: [503],
  },
  {
    method: "post",
    path: "/places/ai-lookup",
    operationId: "aiLookupPlace",
    summary: "Look up a place with Gemini and Google Search grounding",
    description:
      "Gemini place resolver grounded with Google Search. Finds the exact name, address, categories, " +
      "best visit times and tips for venues, review snippets, or natural queries. A coordinate is " +
      "returned only when the model cited a Google Maps link that carries one — never estimated.",
    tags: ["places"],
    auth: true,
    request: {
      body: AiPlaceLookupRequest,
    },
    responses: {
      200: { schema: AiPlaceLookupResponse, description: "Grounded place candidates. `location` may be null." },
    },
  },
  {
    method: "get",
    path: "/places/:id",
    operationId: "getPlace",
    summary: "The full place card",
    tags: ["places"],
    auth: true,
    request: { params: byId },
    responses: { 200: { schema: Place, description: "The place card (AC-2.9)." } },
  },
  {
    method: "patch",
    path: "/places/:id",
    operationId: "updatePlace",
    summary: "Edit a place",
    description: "Omitted fields are left unchanged. This is what the edit form sends (AC-2.9c).",
    tags: ["places"],
    auth: true,
    request: { params: byId, body: UpdatePlaceRequest },
    responses: { 200: { schema: Place, description: "The updated place card." } },
  },
  {
    method: "delete",
    path: "/places/:id",
    operationId: "deletePlace",
    summary: "Delete a place",
    description:
      "Soft delete. Reports how many itinerary items referenced it, and which areas it leaves unused, " +
      "so the client can warn the user and prune its vocabulary without a second request (AC-2.9d).",
    tags: ["places"],
    auth: true,
    request: { params: byId },
    responses: { 200: { schema: DeletePlaceResponse, description: "What the deletion touched." } },
  },
  {
    method: "post",
    path: "/places/:id/merge",
    operationId: "mergePlace",
    summary: "Fold a duplicate into this place",
    description:
      "The user's explicit merge decision (AC-3.5). `:id` survives and `duplicate_place_id` is " +
      "soft-deleted; the caller's rating, the source rows and the area links move to the survivor in " +
      "one transaction, because a merge that stopped halfway would leave provenance pointing at a " +
      "place that is about to vanish. The server never merges on its own — a flag from the rule is " +
      "not proof, and the user is the one who knows whether two nearby places are one.",
    tags: ["places"],
    auth: true,
    request: { params: byId, body: MergePlaceRequest },
    responses: {
      200: { schema: MergePlaceResponse, description: "The surviving place, with the duplicate folded in." },
    },
  },

  // --------------------------------------------------------------------------------- areas
  {
    method: "get",
    path: "/areas",
    operationId: "listAreas",
    summary: "The caller's area vocabulary",
    description:
      "Folded-matched on `?q=`, so typing `cau dat` finds `Cầu Đất`. The folding rule lives in the " +
      "database, which is what keeps the API, a background job and an app release from disagreeing " +
      "about what counts as the same name.",
    tags: ["areas"],
    auth: true,
    request: { query: z.object({ q: z.string().optional() }) },
    responses: { 200: { schema: AreaList, description: "The caller's own areas." } },
  },
  {
    method: "post",
    path: "/areas",
    operationId: "createArea",
    summary: "Add an area, or get the existing one",
    description:
      "Idempotent on the folded name: asking for `da lat` when `Đà Lạt` exists returns the existing " +
      "area with 200 rather than creating a second. `created` says which happened, so a client that " +
      "already holds the vocabulary can skip a refetch.",
    tags: ["areas"],
    auth: true,
    request: { body: CreateAreaRequest },
    responses: { 200: { schema: AreaResponse, description: "The area, and whether this call created it." } },
  },
  // -------------------------------------------------------------------------------------- trips
  {
    method: "get",
    path: "/trips",
    operationId: "listTrips",
    summary: "The caller's trips",
    tags: ["trips"],
    auth: true,
    request: { query: PageQuery },
    responses: { 200: { schema: TripPage, description: "A page of trips, newest first." } },
  },
  {
    method: "post",
    path: "/trips",
    operationId: "createTrip",
    summary: "Create a trip",
    description:
      "A trip can exist before it has places. Constraints are optional and default to values a " +
      "first-time user would choose, because a screen that demands eight numbers before showing a plan " +
      "is a screen that goes unused.",
    tags: ["trips"],
    auth: true,
    request: { body: CreateTripRequest },
    responses: { 201: { schema: TripResponse, description: "The new trip, its constraints and its selection." } },
  },
  {
    method: "get",
    path: "/trips/:id",
    operationId: "getTrip",
    summary: "One trip with its constraints and selection",
    tags: ["trips"],
    auth: true,
    request: { params: byId },
    responses: { 200: { schema: TripResponse, description: "The trip." } },
  },
  {
    method: "patch",
    path: "/trips/:id",
    operationId: "updateTrip",
    summary: "Edit a trip",
    description:
      "Omitted fields are left alone. `place_ids` replaces the selection rather than adding to it, so " +
      "removing a place needs no second endpoint.",
    tags: ["trips"],
    auth: true,
    request: { params: byId, body: UpdateTripRequest },
    responses: { 200: { schema: TripResponse, description: "The updated trip." } },
  },
  {
    method: "delete",
    path: "/trips/:id",
    operationId: "deleteTrip",
    summary: "Delete a trip",
    description: "Soft delete. The places it referenced are untouched.",
    tags: ["trips"],
    auth: true,
    request: { params: byId },
    responses: { 204: { description: "Deleted." } },
  },

  // -------------------------------------------------------------------------------------- plans
  {
    method: "post",
    path: "/trips/:id/plans",
    operationId: "createPlans",
    summary: "Generate plan variants",
    description:
      "Computes every strategy's plan and stores its metrics. The numbers here are the planner's own " +
      "(AC-4.2) and are never model output — the database comment on `plan_variants` says so. A " +
      "constraint that could not be satisfied is returned in `unsatisfied` rather than silently " +
      "ignored, because the client has to show which one gives (AC-4.5).",
    tags: ["plans"],
    auth: true,
    request: { params: byId, body: CreatePlansRequest },
    responses: { 201: { schema: CreatePlansResponse, description: "The variants, with the state of the routing provider." } },
    errors: [422, 503],
  },
  {
    method: "get",
    path: "/trips/:id/plans",
    operationId: "listPlanVariants",
    summary: "The plans this trip already has",
    description:
      "Reads back what was generated and stored, so a plan survives leaving the screen that made it. " +
      "Without this the only way to see an itinerary was to generate it again, which is why a saved " +
      "trip could not be reopened. Empty is a valid answer: a trip exists before it has plans.",
    tags: ["plans"],
    auth: true,
    request: { params: byId },
    responses: { 200: { schema: TripPlansResponse, description: "The stored variants and their selection." } },
  },
  {
    method: "get",
    path: "/trips/:id/plans/:variantId",
    operationId: "getPlanVariant",
    summary: "One plan in full",
    description:
      "Days, stops and legs. `distance_m` and `driving_min` are null where the routing provider did " +
      "not answer, and the client renders 'chưa xác định' rather than substituting an estimate (§18).",
    tags: ["plans"],
    auth: true,
    request: { params: z.object({ id: Id, variantId: Id }) },
    responses: { 200: { schema: PlanVariantDetail, description: "The plan, its days and its legs." } },
  },
  {
    method: "post",
    path: "/trips/:id/plans/:variantId/select",
    operationId: "selectPlan",
    summary: "Choose this plan",
    description: "Marks one variant selected and clears the others, and moves the trip to `planned`.",
    tags: ["plans"],
    auth: true,
    request: { params: z.object({ id: Id, variantId: Id }), body: SelectPlanRequest },
    responses: { 200: { schema: PlanVariant, description: "The selected variant." } },
  },
  {
    method: "get",
    path: "/trips/:id/plans/:variantId/explanations",
    operationId: "getPlanExplanations",
    summary: "Why this plan looks the way it does",
    description:
      "Every reason carries its evidence, and a reason without evidence is not returned at all — an " +
      "explanation nobody can check is worse than none. `generated_by: \"planner\"` is the degraded " +
      "state from AC-6.1: the facts came with no wording, so the client renders the evidence list.",
    tags: ["plans"],
    auth: true,
    request: { params: z.object({ id: Id, variantId: Id }) },
    responses: { 200: { schema: PlanExplanationsResponse, description: "The reasons and their numbers." } },
  },
  {
    method: "get",
    path: "/trips/:id/plans/:variantId/days/:dayIndex/route",
    operationId: "getDayRoute",
    summary: "One day's road path",
    tags: ["plans"],
    auth: true,
    request: { params: z.object({ id: Id, variantId: Id, dayIndex: z.coerce.number().int().gte(1) }) },
    responses: { 200: { schema: TripRoutesResponse, description: "The day's geometry and legs." } },
  },
  {
    method: "put",
    path: "/trips/:id/plans/:variantId/days/:dayIndex/order",
    operationId: "putDayOrder",
    summary: "Set the complete order of one day",
    description:
      "The whole list, not one moved stop. A partial request is rejected because a server that " +
      "received only the moved item cannot tell where it came from, and guessing would silently " +
      "reorder the day when two devices edit at once (AC-2.6).",
    tags: ["plans"],
    auth: true,
    request: { params: z.object({ id: Id, variantId: Id, dayIndex: z.coerce.number().int().gte(1) }), body: PutDayOrderRequest },
    responses: { 204: { description: "Order applied." } },
    errors: [409],
  },

  // ------------------------------------------------------------------------------------ routing
  {
    method: "post",
    path: "/routes",
    operationId: "previewRoute",
    summary: "A road path through points the caller supplies",
    description:
      "What the arrange step draws: the day the user is laying out, before there is a trip to hang it " +
      "on and before there is a plan. Nothing is stored — the caller sent the points and gets a road " +
      "back, in the order it was driven (`order`), with `distances_known: false` and no geometry when " +
      "the provider did not answer rather than an estimated line.\n\n" +
      "The request states how the party moves (`transport`) and the response names the routing profile " +
      "that became (`profile`), because a bicycle and a car through the same points are two different " +
      "roads. `fixed` says whether the order given is the answer or only the input: the arrange step " +
      "asks for the shortest order as a *suggestion* (`false`) and drives the arrangement (`true`), and " +
      "a generated plan always drives the arrangement.\n\n" +
      "Not a second planner. Which points a day has — the anchors, the midday return, the slot order — " +
      "is decided by the engine's own `assembleDay`, which the client runs too, so the picture and the " +
      "plan are the same day asked about twice.",
    tags: ["routes"],
    auth: true,
    request: { body: RoutePreviewRequest },
    responses: { 200: { schema: RoutePreviewResponse, description: "The road, its order, and its totals." } },
    errors: [400],
  },

  // ------------------------------------------------------------------------------------ weather
  {
    method: "get",
    path: "/trips/:id/weather",
    operationId: "getTripWeather",
    summary: "Forecast for the trip's window",
    description:
      "A trip past the forecast horizon returns `status: \"out_of_range\"` with no days, which is a " +
      "normal case and not an outage — measured, the provider answers HTTP 400 past its horizon. The " +
      "client hides the weather row rather than estimating (AC-6.2).",
    tags: ["trips"],
    auth: true,
    request: { params: byId },
    responses: { 200: { schema: TripWeatherResponse, description: "The forecast, or why there is none." } },
  },

  // ------------------------------------------------------------------------------------- fund
  {
    method: "get",
    path: "/trips/:id/fund",
    operationId: "getTripFund",
    summary: "The trip's fund, its contributors and its ledger",
    description:
      "One read: the pot, Σ contributions, Σ expenses, the number of rows, each participant's total, " +
      "and every row in day-then-record order with the pot after it. `pot_vnd` is null when nothing is " +
      "recorded — never 0 — and may be negative when an expense over-draws the fund, which is reported " +
      "rather than refused.",
    tags: ["fund"],
    auth: true,
    request: { params: byId },
    responses: { 200: { schema: TripFundResponse, description: "The fund as it currently stands." } },
  },
  {
    method: "post",
    path: "/trips/:id/fund/participants",
    operationId: "createFundParticipant",
    summary: "Add a person who may contribute",
    description:
      "A participant is trip data, not an account. The folded name is the trip-local key, so adding a " +
      "name that already exists (by diacritic-folded match) is a 409 rather than a second person.",
    tags: ["fund"],
    auth: true,
    request: { params: byId, body: CreateFundParticipantRequest },
    responses: { 201: { schema: FundParticipant, description: "The new participant, with no contributions yet." } },
    errors: [409],
  },
  {
    method: "patch",
    path: "/trips/:id/fund/participants/:participantId",
    operationId: "updateFundParticipant",
    summary: "Rename a contributor",
    description:
      "Only the name changes. A participant's money is the ledger's, so a rename moves no figure and " +
      "touches no row but this one. The folded name is the trip-local key, so renaming onto a name the " +
      "trip already has (by diacritic-folded match) is a 409 rather than two people of one name.",
    tags: ["fund"],
    auth: true,
    request: { params: byParticipant, body: UpdateFundParticipantRequest },
    responses: { 200: { schema: FundParticipant, description: "The renamed participant, their total unchanged." } },
    errors: [409],
  },
  {
    method: "delete",
    path: "/trips/:id/fund/participants/:participantId",
    operationId: "deleteFundParticipant",
    summary: "Remove a person from the fund panel",
    description:
      "Refused — 409 — when the participant has a transaction in the fund: their recorded money is the " +
      "ledger's, and the panel's totals must keep summing to Σ contributions, so a member who has " +
      "contributed cannot be removed. With no transactions the removal is soft: the participant leaves " +
      "the panel and the name is freed, while the ledger is untouched. Whether a member may be deleted " +
      "is the server's fact, never the client's guess.",
    tags: ["fund"],
    auth: true,
    request: { params: byParticipant },
    responses: { 204: { description: "Removed. The ledger is untouched." } },
    errors: [409],
  },
  {
    method: "post",
    path: "/trips/:id/fund/contributions",
    operationId: "recordFundContribution",
    summary: "Record money into the fund",
    description: "Integer đồng, greater than zero, attributed to a participant of this trip.",
    tags: ["fund"],
    auth: true,
    request: { params: byId, body: RecordFundContributionRequest },
    responses: { 201: { schema: FundEntry, description: "The recorded row, with the pot after it." } },
  },
  {
    method: "post",
    path: "/trips/:id/fund/expenses",
    operationId: "recordFundExpense",
    summary: "Record money out of the fund",
    description:
      "Drawn from the fund whether or not the money is there: the pot may go negative, which is a fact " +
      "to show and not a refusal. An expense names no payer.",
    tags: ["fund"],
    auth: true,
    request: { params: byId, body: RecordFundExpenseRequest },
    responses: { 201: { schema: FundEntry, description: "The recorded row, with the pot after it." } },
  },
  {
    method: "patch",
    path: "/trips/:id/fund/contributions/:entryId",
    operationId: "updateFundContribution",
    summary: "Correct a contribution in place",
    description:
      "A correction is not a financial event: the row is edited, so its `created_at` and its position " +
      "never move, and `edited_at` is set. An omitted field is left alone.",
    tags: ["fund"],
    auth: true,
    request: { params: byEntry, body: UpdateFundEntryRequest },
    responses: { 200: { schema: FundEntry, description: "The corrected row." } },
  },
  {
    method: "delete",
    path: "/trips/:id/fund/contributions/:entryId",
    operationId: "deleteFundContribution",
    summary: "Delete a contribution",
    description: "Removes the row. The pot moves back by its amount; nothing is kept of what it was.",
    tags: ["fund"],
    auth: true,
    request: { params: byEntry },
    responses: { 204: { description: "Deleted." } },
  },
  {
    method: "patch",
    path: "/trips/:id/fund/expenses/:entryId",
    operationId: "updateFundExpense",
    summary: "Correct an expense in place",
    description:
      "A correction is not a financial event: the row is edited, so its `created_at` and its position " +
      "never move, and `edited_at` is set. An omitted field is left alone.",
    tags: ["fund"],
    auth: true,
    request: { params: byEntry, body: UpdateFundEntryRequest },
    responses: { 200: { schema: FundEntry, description: "The corrected row." } },
  },
  {
    method: "delete",
    path: "/trips/:id/fund/expenses/:entryId",
    operationId: "deleteFundExpense",
    summary: "Delete an expense",
    description: "Removes the row. The pot moves back by its amount; nothing is kept of what it was.",
    tags: ["fund"],
    auth: true,
    request: { params: byEntry },
    responses: { 204: { description: "Deleted." } },
  },
] as const satisfies readonly RouteDefinition[];

/** Every operation id in the registry, as a literal union. The client's method names come from this. */
export type OperationId = (typeof ROUTES)[number]["operationId"];

/** The error statuses a route can return, base set plus its own. */
export function errorStatusesOf(route: RouteDefinition): ProblemStatus[] {
  const merged = new Set<ProblemStatus>(route.auth ? BASE_ERRORS : []);
  for (const status of route.errors ?? []) merged.add(status);
  return [...merged].sort((a, b) => a - b);
}

export type { ProviderStatus };
